import { endOfDay, format } from 'date-fns'
import get from 'lodash-es/get'
import has from 'lodash-es/has'
import isArray from 'lodash-es/isArray'
import isBoolean from 'lodash-es/isBoolean'
import isNumber from 'lodash-es/isNumber'
import isString from 'lodash-es/isString'
import map from 'lodash-es/map'
import mapValues from 'lodash-es/mapValues'
import omit from 'lodash-es/omit'

import {
  DATE_API_DATA_FORMAT,
  DATE_SHORT_FORMAT,
} from '../../../constants/date'
import { PAGINATION_ALL_ITEMS_SIZE } from '../../../constants/pagination'
import { getFilterSingleFieldData } from '../Filter.api'
import { getFilterSingleFieldUrlParams } from '../Filter.utils'

import { FILTER_BAR_FILTER_TRIGGER_VARIANT } from './FilterBar.constants'

export function checkIsCompactFilterTrigger(filterTriggerVariant) {
  return filterTriggerVariant === FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON
}

export function parsePermanentCommonFilters(filters) {
  let params = []

  if (filters && isBoolean(filters.archived)) {
    params = params.concat([['archived', filters.archived]])
  }

  if (filters && isBoolean(filters.unread)) {
    params = params.concat([['unread', filters.unread]])
  }

  return params
}

export function getNextFilterOptionsByMultiSelect({
  filterOptions,
  key,
  value,
}) {
  if (filterOptions[key].find(option => option.value === value.value)) {
    const nextOptions = filterOptions[key].filter(i => value.value !== i.value)

    if (nextOptions.length > 0) {
      return {
        ...filterOptions,
        [key]: nextOptions,
      }
    } else {
      return omit(filterOptions, [key])
    }
  } else {
    return {
      ...filterOptions,
      [key]: filterOptions[key].concat(value),
    }
  }
}

export function getNextFilterOptionsByDate({
  endParam,
  filterOptions,
  startParam,
  value,
}) {
  if (get(filterOptions, [startParam, 'value']) === value || !value) {
    return omit(filterOptions, [startParam, endParam])
  } else {
    const startDateValue = format(value, DATE_API_DATA_FORMAT)

    const endDateValue = format(endOfDay(value), DATE_API_DATA_FORMAT)

    return {
      ...filterOptions,
      [endParam]: {
        key: endParam,
        label: format(value, DATE_SHORT_FORMAT),
        value: endDateValue,
      },
      [startParam]: {
        key: startParam,
        label: format(value, DATE_SHORT_FORMAT),
        value: startDateValue,
      },
    }
  }
}

export function getNextFilterOptions({ filterOptions, isMulti, key, value }) {
  if (!filterOptions) {
    return { [key]: [value] }
  } else if (!value) {
    return omit(filterOptions, [key])
  } else if (!filterOptions[key] || !isMulti) {
    return { ...filterOptions, [key]: value }
  } else if (isMulti) {
    return getNextFilterOptionsByMultiSelect({ filterOptions, key, value })
  }
}

export function getNextFilterParams(options, filterOptions) {
  const currentFilterOptions = options || filterOptions

  return mapValues(currentFilterOptions, option => {
    if (isString(option) || isNumber(option)) {
      return option
    }

    if (isArray(option) && option.length === 0) {
      return undefined
    }

    if (isBoolean(option)) {
      return option
    }

    if (has(option, 'value')) {
      return option.value
    }

    return map(option, 'value')
  })
}

export function getDateValue(value, key) {
  if (key.includes('_lte')) {
    return endOfDay(value)
  }

  return value
}

export function getDateRangeOptions({ key, options, value }) {
  if (!value) {
    return omit(options, [key])
  }

  return {
    ...options,
    [key]: {
      key: key,
      label: format(value, DATE_SHORT_FORMAT),
      value: format(getDateValue(value, key), DATE_API_DATA_FORMAT),
    },
  }
}

export function getDateOption({ nextOptionsProps, ...restParams }) {
  const newOptions = getDateRangeOptions(restParams)

  if (nextOptionsProps) {
    return getDateOption({ ...nextOptionsProps, options: newOptions })
  }

  return newOptions
}

export function getAfterClearedFilterOptions({
  additionalKey,
  filterKey,
  filterOptions,
  permanentFilters,
}) {
  if (filterKey || additionalKey) {
    const nextFilters = omit(filterOptions, [filterKey, additionalKey])

    const nextFiltersValues = mapValues(nextFilters, nextFilter =>
      map(nextFilter, 'value')
    )

    return { ...nextFiltersValues, ...permanentFilters }
  }

  return permanentFilters
}

export async function getFilterData({
  apiUrl,
  entity,
  parsePermanentFilters,
  permanentFilters,
  ...apiParams
}) {
  const params = getFilterSingleFieldUrlParams({
    ...apiParams,
    permanentFilters: parsePermanentFilters(permanentFilters),
  })

  return getFilterSingleFieldData({ apiUrl, entity, params })
}

export function getDataParams(item) {
  return {
    field_name: item[0],
    items: item[1],
    size: PAGINATION_ALL_ITEMS_SIZE,
  }
}

export function getTemplateValue(option) {
  return option.id
}

export function getTemplateLabel(option) {
  return option.name
}

export function hasEmptyFilterValues(nextOptions) {
  return Object.values(nextOptions).some(
    nextOptionValue => nextOptionValue.length === 0
  )
}
